import "./loading.css";

/**
 * Boot/asset-load gate shown between the menu and the live scene. Kills the
 * blank-frame "is it broken?" bounce while `loadWorld()` fetches the heightmap,
 * doodads, meshes and audio (launch P0).
 *
 * `loadWorld()` is a single parallel `Promise.all` with no granular progress, so
 * the bar is milestone-driven: it auto-trickles toward a ceiling during the big
 * await (never looks frozen) and snaps to real checkpoints the caller reports
 * via `milestone()`. `finish()` runs it to 100% and fades the overlay out — the
 * caller invokes it on the first painted frame so the scene is real before the
 * art lifts.
 *
 * Art: `/assets/ui/loading-bg.jpg` (the locked Mythwild low-poly key art). The
 * overlay is dark from frame 0 and fades the art in on decode, so a slow image
 * fetch never shows an empty screen.
 */

const TIPS: readonly string[] = [
  "Right-click to move, harvest, and attack.",
  "Press Z to open the build menu.",
  "Build a campfire to survive the cold nights.",
  "Gather wood, stone and food to grow your tribe.",
  "Spend skill points to unlock new abilities.",
  "Buy abilities at the Tavern with gold.",
  "Press Space to recenter the camera on your hero.",
  "Outlast every rival tribe to win.",
];

const TAGLINE = "Build a tribe · Survive the stone age · Outlast your rivals";
const TRICKLE_CEILING = 0.85; // auto-fill stops here until milestones push it up
const TIP_INTERVAL_MS = 3200;
const TICK_MS = 140;

export interface LoadingScreenApi {
  /** Show the overlay and start the trickle + tip rotation. Idempotent. */
  show(): void;
  /** Raise the progress floor to `p` (0..1) — snaps the bar to a real checkpoint. */
  milestone(p: number): void;
  /** Run to 100% and fade out; cleans up timers + DOM. Idempotent. */
  finish(): void;
}

export function createLoadingScreen(): LoadingScreenApi {
  const root = document.createElement("div");
  root.id = "loading-screen";
  root.innerHTML = `
    <div class="ls-art" style="background-image:url('/assets/ui/loading-bg.jpg')"></div>
    <div class="ls-scrim"></div>
    <div class="ls-tip"></div>
    <div class="ls-foot">
      <div class="ls-logo">TRIBEZ</div>
      <div class="ls-tag">${TAGLINE}</div>
      <div class="ls-bar"><i></i></div>
      <div class="ls-label">Loading world…</div>
    </div>`;

  const artEl = root.querySelector(".ls-art") as HTMLElement;
  const tipEl = root.querySelector(".ls-tip") as HTMLElement;
  const fillEl = root.querySelector(".ls-bar > i") as HTMLElement;

  let shown = false;
  let finished = false;
  let displayed = 0; // 0..1 currently rendered
  let ceiling = TRICKLE_CEILING;
  let tickTimer: ReturnType<typeof setInterval> | null = null;
  let tipTimer: ReturnType<typeof setInterval> | null = null;
  let tipIdx = 0;

  function paint(): void {
    fillEl.style.width = `${Math.round(displayed * 100)}%`;
  }

  function tick(): void {
    // Ease toward the ceiling; the closer it gets the slower it creeps, so a long
    // load decelerates near the ceiling instead of stalling at a hard stop.
    const target = ceiling;
    if (displayed < target) {
      displayed += (target - displayed) * 0.08 + 0.004;
      if (displayed > target) displayed = target;
      paint();
    }
  }

  function rotateTip(): void {
    tipEl.classList.add("ls-tip-fade");
    setTimeout(() => {
      tipIdx = (tipIdx + 1) % TIPS.length;
      tipEl.textContent = TIPS[tipIdx]!;
      tipEl.classList.remove("ls-tip-fade");
    }, 350);
  }

  function show(): void {
    if (shown) return;
    shown = true;
    // Fade the art in once it has decoded (overlay is already dark underneath).
    const img = new Image();
    img.onload = () => root.classList.add("ls-art-ready");
    img.src = "/assets/ui/loading-bg.jpg";

    tipIdx = Math.floor(TIPS.length * pseudoRandom()); // vary the first tip per boot
    tipEl.textContent = TIPS[tipIdx]!;
    displayed = 0.06;
    paint();
    document.body.appendChild(root);
    tickTimer = setInterval(tick, TICK_MS);
    tipTimer = setInterval(rotateTip, TIP_INTERVAL_MS);
  }

  function milestone(p: number): void {
    ceiling = Math.max(ceiling, Math.min(1, p));
  }

  function finish(): void {
    if (finished) return;
    finished = true;
    if (tickTimer) { clearInterval(tickTimer); tickTimer = null; }
    if (tipTimer) { clearInterval(tipTimer); tipTimer = null; }
    displayed = 1;
    paint();
    // Hold a beat at 100% so the fill visibly completes, then fade + remove.
    setTimeout(() => {
      root.classList.add("ls-hidden");
      setTimeout(() => root.remove(), 600);
    }, 320);
  }

  return { show, milestone, finish };
}

// Avoids Math.random (banned in some harness contexts) while still varying the
// opening tip across boots — seeded off the high-res clock.
function pseudoRandom(): number {
  const t = performance.now();
  return (t - Math.floor(t));
}
